import React, { FC, memo, ReactElement, useMemo, useState } from 'react'
import { AssetEntryMap, HelpEntry } from '../../API/Contentful/Types'
import { ReactState } from '../../Contexts'
import { FAB } from '../FAB/FAB'
import { IconName } from '../Icon'
import Modal from '../Modal'
import { WYSIWYGDisplay } from '../WYSIWYGDisplay/WYSIWYGDisplay'
import { isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import Button from '../Button'
import { RoundedButton } from '../../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { IconSize } from '../../../../NonReact/constants/React/Icon/Constants'

type Props = {
    className?: string
    helpEntry: Definable<HelpEntry>
    assets: Definable<AssetEntryMap>
    buttonLabel?: string
    iconSize?: IconSize
    isRouterMode?: boolean
}

/**
 * Display a help entry in a modal with data from the Contentful API.
 *
 * @param className - The class name to apply to the component.
 * @param helpEntry - The help entry to display.
 * @param assets - The assets to display from contentful in the contentful AssetEntryMap format.
 * @param buttonLabel - The label to display on the button.
 */
export const HelpEntryModal: FC<Props> = memo(
    ({ className, helpEntry, assets, buttonLabel, iconSize, isRouterMode }: Props): ReactElement => {
        const [isOpen, setIsOpen]: ReactState<boolean> = useState<boolean>(false)

        const fullButton: ReactElement = useMemo(
            (): ReactElement =>
                isRouterMode ? (
                    <RoundedButton
                        onClick={(): void => setIsOpen(true)}
                        type='button'
                        className='help-entry-modal-trigger'
                        isOutlined
                        shrink
                    >
                        {buttonLabel}
                    </RoundedButton>
                ) : (
                    <Button
                        label={buttonLabel}
                        onClick={(): void => setIsOpen(true)}
                        type='button'
                        customClass='help-entry-modal-trigger'
                        isOutlined
                    />
                ),
            [isRouterMode]
        )

        return helpEntry ? (
            <div className={className}>
                {isStringNotEmpty(buttonLabel) ? (
                    fullButton
                ) : (
                    <FAB
                        iconName={IconName.QUESTION_MARK}
                        onClick={(): void => setIsOpen(true)}
                        isExtended={false}
                        className='help-entry-modal-trigger'
                        iconSize={iconSize}
                    />
                )}
                <Modal
                    classNames='help-entry-modal'
                    isOpen={isOpen}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    onClose={(): void => setIsOpen(false)}
                >
                    <WYSIWYGDisplay content={helpEntry.content} assets={assets} />
                </Modal>
            </div>
        ) : (
            <></>
        )
    }
)
